import { Typography } from '@mui/material'
import {
  ConfirmationDialog,
  ConfirmationDialogProps,
} from 'features/ConfirmationDialog/ConfirmationDialog'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Debugger } from './debugger'

type BlockedAction = (() => void) | null

type UseUnsavedChangesGuardOptions = {
  beforeUnload?: boolean
  texts?: {
    title: string
    body: string | React.ReactNode
    saveChanges: string
    discardChanges: string
    cancel: string
  }
  /**
   * Whether to use the dialog manager to show confirmation dialogs
   * or render the ConfirmDialog component returned by the hook manually.
   */
  manuallyRenderConfirmDialog?: boolean
}

type WrapOptions = {
  onSave?: () => void | Promise<void>
  cleanAfterSave?: boolean
  autoSave?: boolean
}

export type BlockedActionHandler = {
  action: () => void
  actionName: string
  opts?: WrapOptions
}

export function useUnsavedChangesGuardOptions(
  options?: UseUnsavedChangesGuardOptions,
) {
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)
  const [isBusy, setBusy] = useState(false)

  const pendingActionRef = useRef<BlockedAction>(null)
  const pendingSaveRef = useRef<WrapOptions['onSave']>(null)
  const pendingSaveActionName = useRef<string | null>(null)
  const cleanAfterSaveRef = useRef<boolean>(true)
  const autoSaveRef = useRef<boolean>(false)
  const idleCallbackRef = useRef<number | null>(null)

  const {
    beforeUnload = true,
    texts = {
      title: options?.texts?.title || t('common:navigation-confirm-title'),
      body: options?.texts?.body || (
        <>
          <Typography variant="body2" color="textSecondary">
            {t('common:navigation-confirm-body')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {t(`project:save-action-${pendingSaveActionName.current}`)}
          </Typography>
        </>
      ),
      saveChanges:
        options?.texts?.saveChanges || t('common:navigation-confirm-save'),
      discardChanges:
        options?.texts?.discardChanges ||
        t('common:navigation-confirm-discardChanges'),
      cancel: options?.texts?.cancel || t('common:cancel'),
    },
  } = options ?? {}

  const [isDirty, setIsDirty] = useState(false)

  const markClean = useCallback((actionName: string) => {
    Debugger.Info(`[unsaved]: markClean called for action: ${actionName}`)

    pendingSaveRef.current = null
    pendingActionRef.current = null

    if (idleCallbackRef.current != null) {
      cancelIdleCallback(idleCallbackRef.current)
    }
    idleCallbackRef.current = requestIdleCallback(() => {
      idleCallbackRef.current = null
      setIsDirty(false)
    })
  }, [])

  useEffect(() => {
    if (!beforeUnload) {
      return () => {}
    }

    const controller = new AbortController()

    window.addEventListener(
      'beforeunload',
      (e) => {
        if (isDirty) {
          e.preventDefault()
          // Chrome requires returnValue to be set
          e.returnValue = ''
        }
      },
      { signal: controller.signal },
    )

    return () => {
      controller.abort()
    }
  }, [isDirty, beforeUnload])

  /**
   * open confirm dialog for a blocked action
   */
  const confirmTransition = useCallback(
    (action: () => void, opts?: WrapOptions) => {
      pendingActionRef.current = action
      cleanAfterSaveRef.current = opts?.cleanAfterSave ?? true
      autoSaveRef.current = opts?.autoSave ?? false
      setOpen(true)
    },
    [],
  )

  /**
   * wrap any action (navigation, tab change, etc.) that should be blocked when there are unsaved changes
   */
  const wrapBlockedAction = useCallback(
    (args: BlockedActionHandler) => {
      Debugger.Info(
        `[unsaved]: wrapBlockedAction for action: ${pendingSaveActionName}`,
        {
          isDirty,
        },
      )

      return async () => {
        Debugger.Info(
          `[unsaved]: Executing wrapped action: ${args.actionName}`,
          {
            isDirty,
            action: args.action.toString(),
          },
        )

        if (!isDirty) {
          args.action()
          return
        }

        confirmTransition(args.action, args.opts)
      }
    },
    [confirmTransition, isDirty],
  )

  const { openDialog, closeDialog } = useDialogManager()

  const handleCancel = useCallback(
    (actionName: string) => {
      Debugger.Info(`[unsaved]: Canceling action: ${actionName}`)
      setOpen(false)
      setConfirmationOpen(false)
      if (!options?.manuallyRenderConfirmDialog) {
        closeDialog()
      }
    },
    [closeDialog, options?.manuallyRenderConfirmDialog],
  )

  const markDirty = useCallback(
    (onSave: WrapOptions['onSave'], actionName: string) => {
      Debugger.Info(`[unsaved]: Marking dirty due to action: ${actionName}`)

      pendingSaveRef.current = onSave
      pendingSaveActionName.current = actionName

      if (idleCallbackRef.current != null) {
        cancelIdleCallback(idleCallbackRef.current)
      }
      idleCallbackRef.current = requestIdleCallback(() => {
        idleCallbackRef.current = null
        setIsDirty(true)
      })
    },
    [setIsDirty],
  )

  const [confirmationOpen, setConfirmationOpen] = useState(false)

  const handleOpenOrCloseConfirmation = useCallback(
    (
      open: boolean,
      handleSave: (
        pendingAction: () => void,
        pendingSave: () => void,
      ) => Promise<void>,
      handleDiscardChanges: (actionName: string) => void,
    ) => {
      const pendingAction = pendingActionRef.current
      const pendingSave = pendingSaveRef.current

      if (options?.manuallyRenderConfirmDialog) {
        setConfirmationOpen(open)
      } else {
        if (open) {
          openDialog('ConfirmationDialog', {
            onConfirm: async () => {
              await handleSave(pendingAction, pendingSave)
            },
            onCancel: handleCancel,
            title: texts.title,
            message: texts.body,
            confirmText: texts.saveChanges,
            cancelText: texts.cancel,
            disableAutoCloseOnConfirm: true,
            secondaryAction: {
              label: texts.discardChanges,
              action: handleDiscardChanges,
              color: 'error',
              closeAfterRun: true,
            },
            busy: isBusy,
            open,
          } as const as ConfirmationDialogProps)
        } else {
          closeDialog()
        }
      }
    },

    [
      closeDialog,
      handleCancel,
      isBusy,
      openDialog,
      options?.manuallyRenderConfirmDialog,
      texts.body,
      texts.cancel,
      texts.discardChanges,
      texts.saveChanges,
      texts.title,
    ],
  )

  const handleSave = useCallback(
    async (
      pendingAction: () => void,
      pendingSave: () => void | Promise<void>,
    ) => {
      setBusy(true)

      try {
        if (pendingSave) {
          await pendingSave()
          setIsDirty(false)
        }

        setOpen(false)
        setConfirmationOpen(false)
        closeDialog()

        pendingActionRef.current = null
        pendingSaveRef.current = undefined

        pendingAction?.()
      } catch (err) {
        setOpen(false)
        setIsDirty(true)
        setConfirmationOpen(false)
      } finally {
        setBusy(false)
      }
    },
    [closeDialog],
  )

  const handleDiscardChanges = useCallback(
    (actionName: string) => {
      Debugger.Info(
        `[unsaved]: Discarding changes and proceeding with action: ${actionName}`,
      )

      const action = pendingActionRef.current

      pendingActionRef.current = null
      pendingSaveRef.current = null

      setConfirmationOpen(false)
      closeDialog()
      setOpen(false)
      setIsDirty(false)
      markClean(actionName)

      action?.()
    },
    [closeDialog, markClean],
  )

  useEffect(() => {
    if (isBusy) {
      return
    }

    const pendingAction = pendingActionRef.current
    const pendingSave = pendingSaveRef.current
    const isAutoSave = autoSaveRef.current

    if (!isDirty && !pendingSave && pendingAction) {
      pendingActionRef.current = null
      pendingAction()
      return
    }

    if (open && isDirty && pendingSave) {
      if (isAutoSave) {
        handleSave(pendingAction, pendingSave)
        return
      } else {
        handleOpenOrCloseConfirmation(true, handleSave, handleDiscardChanges)
      }
    }
  }, [
    handleDiscardChanges,
    handleOpenOrCloseConfirmation,
    handleSave,
    isBusy,
    isDirty,
    open,
    options?.manuallyRenderConfirmDialog,
  ])

  const ConfirmDialog = useMemo(() => {
    const pendingAction = pendingActionRef.current
    const pendingSave = pendingSaveRef.current

    return (
      <ConfirmationDialog
        open={confirmationOpen}
        onConfirm={async () => {
          await handleSave(pendingAction, pendingSave)
        }}
        onCancel={() => handleCancel(pendingSaveActionName.current)}
        title={texts.title}
        message={texts.body}
        confirmText={texts.saveChanges}
        cancelText={texts.cancel}
        disableAutoCloseOnConfirm={true}
        secondaryAction={{
          label: texts.discardChanges,
          action: () =>
            handleDiscardChanges(pendingSaveActionName.current || ''),
          color: 'error',
          closeAfterRun: true,
        }}
        busy={isBusy}
      />
    )
  }, [
    confirmationOpen,
    handleCancel,
    handleDiscardChanges,
    handleSave,
    isBusy,
    texts.body,
    texts.cancel,
    texts.discardChanges,
    texts.saveChanges,
    texts.title,
  ])

  const reset = useCallback(() => {
    Debugger.Info('[unsaved]: reset called — clearing all pending state')

    pendingActionRef.current = null
    pendingSaveRef.current = null
    pendingSaveActionName.current = null

    if (idleCallbackRef.current != null) {
      cancelIdleCallback(idleCallbackRef.current)
      idleCallbackRef.current = null
    }

    setIsDirty(false)
    setOpen(false)
    setConfirmationOpen(false)
  }, [])

  return {
    isDirty,
    markDirty,
    markClean,
    wrapBlockedAction,
    confirmTransition,
    reset,
    /**
     * Confirmation Dialog component to be rendered, if needed
     */
    ConfirmDialog,
  }
}
